import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import PageLoader from '../../components/PageLoader/PageLoader';
import { type DispatchTaskType, buildUnifiedDispatchSearch } from '../../pages/dispatch/utils/dispatch-params';
import { useUnifiedDispatchTaskTypes } from '../../pages/dispatch/utils/use-unified-dispatch-task-types';
import routes from '../../utils/routes';
import type { RouterOutletContext } from '../router';

interface UnifiedDispatchRedirectProps {
    /** Which task type the wrapped legacy route maps to in the unified view. */
    taskType: DispatchTaskType;
}

/**
 * Sends a legacy dispatch route to the unified `/dispatch` view once that view is turned on, preserving
 * the URL's params so deep links keep working.
 *
 * Only redirects once the unified view serves this route's own task type, so a hauler whose roll-off dispatch still
 * lives on the legacy page keeps it while their portable toilet dispatch moves — the nav links and
 * `UnifiedDispatchGuard` read the same source, so the three can't disagree.
 */
const UnifiedDispatchRedirect = ({ taskType }: UnifiedDispatchRedirectProps) => {
    const context = useOutletContext<RouterOutletContext>();
    const location = useLocation();
    const unifiedDispatchTaskTypes = useUnifiedDispatchTaskTypes();

    // Hold the route until the flag resolves so neither the legacy page nor the unified one mounts twice —
    // rendering the legacy `<Outlet />` during that window fires its queries and WebSocket for nothing.
    if (unifiedDispatchTaskTypes === null) {
        return <PageLoader />;
    }

    if (unifiedDispatchTaskTypes.includes(taskType)) {
        return <Navigate to={{ pathname: routes.dispatch.list, search: buildUnifiedDispatchSearch(location.search, taskType) }} replace />;
    }

    return <Outlet context={context} />;
};

export default UnifiedDispatchRedirect;
